import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./AdvisorRanks.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const bp = (n) => `${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} BP`;
const LIMIT = 20;

/** Members' advisor ranks: how many hold each rank, who they are and what their rank earned. */
const AdvisorRanks = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rankId, setRankId] = useState(0);
  const [search, setSearch] = useState("");
  const [applied, setApplied] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (rankId) params.set("rankId", rankId);
    if (applied) params.set("search", applied);
    const { data: res, error } = await apiCall("GET", `/advisor-ranks?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load ranks.");
    setData(res?.data || null);
  }, [page, rankId, applied]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const pick = (id) => {
    setPage(1);
    setRankId((cur) => (cur === id ? 0 : id));
  };

  const recalculate = async () => {
    setBusy(true);
    const { data: res, error } = await apiCall("POST", "/advisor-ranks/recalculate", {});
    setBusy(false);
    if (error) return toast.error(error.message || "Could not recalculate.");
    toast.success(res?.message || "Ranks updated.");
    load();
  };

  const ranks = data?.ranks || [];
  const incomes = data?.incomes || [];
  const rows = data?.members || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  return (
    <div className="plan-settings activation-requests-page advisor-ranks-admin">
      <div className="ps-header">
        <div>
          <h2>Advisor Ranks</h2>
          <p>Ranks held by active IDs (any package). Ranks are checked every morning and on every activation.</p>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <ExportButtons
            title={`Advisor Ranks${rankId ? ` - ${ranks.find((r) => r.rankId === rankId)?.name || ""}` : ""}`}
            filters={[rankId ? `rank: ${ranks.find((r) => r.rankId === rankId)?.name}` : "", applied ? `search: ${applied}` : ""].filter(Boolean).join(", ")}
            columns={[
              { header: "Name", value: (m) => m.name },
              { header: "Username", value: (m) => m.username },
              { header: "Rank", value: (m) => m.rank },
              { header: "Since", value: (m) => (m.achieved_at ? moment(m.achieved_at).format("DD MMM YYYY") : "") },
              { header: "Sponsor", value: (m) => m.sponsor },
              { header: "Direct business", value: (m) => bp(m.direct_bp) },
              { header: "Directs", value: (m) => m.directs },
              { header: "Team", value: (m) => m.team_size },
              ...incomes.map((i) => ({ header: i.name, value: (m) => inr(m.incomes?.[i.slug]) })),
            ]}
            getRows={() =>
              collectPages(async (p, l) => {
                const params = new URLSearchParams({ page: p, limit: l });
                if (rankId) params.set("rankId", rankId);
                if (applied) params.set("search", applied);
                const { data: res } = await apiCall("GET", `/advisor-ranks?${params.toString()}`);
                return { rows: res?.data?.members || [], pages: res?.data?.pages || 1 };
              })
            }
          />
          <Button className="ar-primary-btn" onClick={recalculate} disabled={busy}>
            {busy ? <Spinner size="sm" /> : "Recalculate now"}
          </Button>
        </div>
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat ar-stat--approved">
          <span>Ranked members</span>
          <strong>{data?.ranked ?? "—"}</strong>
          <small>hold a rank</small>
        </div>
        <div className="ps-stat ar-stat">
          <span>Active IDs</span>
          <strong>{data?.active_members ?? data?.advisors ?? "—"}</strong>
          <small>members with an activated package</small>
        </div>
      </div>

      <div className="advr-chips">
        <button type="button" className={`advr-chip${rankId === 0 ? " active" : ""}`} onClick={() => pick(0)}>
          <span>All ranks</span>
          <strong>{data?.ranked ?? 0}</strong>
        </button>
        {ranks.map((r) => (
          <button
            type="button"
            key={r.rankId}
            className={`advr-chip${rankId === r.rankId ? " active" : ""}${r.members ? "" : " empty"}`}
            onClick={() => pick(r.rankId)}
          >
            <span>
              <em>{r.rankId}</em> {r.name}
            </span>
            <strong>{r.members}</strong>
          </button>
        ))}
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Member</Form.Label>
            <Form.Control size="sm" placeholder="Username or name" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="ar-f-reset">
            <Button size="sm" variant="outline-secondary" disabled={!search && !rankId} onClick={() => { setSearch(""); setRankId(0); setPage(1); }}>
              Reset
            </Button>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Rank</th>
                <th>Sponsor</th>
                <th>Direct business</th>
                <th>Team</th>
                {incomes.map((i) => (
                  <th key={i.slug} className="text-end">{i.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5 + incomes.length} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={5 + incomes.length} className="ps-empty">{search || rankId ? "Nothing matches these filters." : "No member holds a rank yet."}</td></tr>
              )}
              {!loading &&
                rows.map((m) => (
                  <tr key={m.uid}>
                    <td>
                      <div className="ps-strong">{m.name || "—"}</div>
                      <div className="ps-muted ps-small">{m.username}</div>
                    </td>
                    <td>
                      <span className="advr-rank"><em>{m.rankId}</em>{m.rank}</span>
                      <div className="ps-muted ps-small">{m.achieved_at ? `Since ${moment(m.achieved_at).format("DD MMM YYYY")}` : ""}</div>
                    </td>
                    <td className="ps-small">{m.sponsor || "—"}</td>
                    <td>
                      <div className="ps-strong">{bp(m.direct_bp)}</div>
                      <div className="ps-muted ps-small">{m.directs} direct{m.directs === 1 ? "" : "s"}</div>
                    </td>
                    <td>{m.team_size}</td>
                    {incomes.map((i) => (
                      <td key={i.slug} className="text-end">{inr(m.incomes?.[i.slug])}</td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * LIMIT + 1 : 0}–{Math.min(page * LIMIT, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>

      {(data?.recent || []).length > 0 && (
        <div className="ps-panel advr-recent">
          <h4>Latest rank ups</h4>
          {data.recent.map((h) => (
            <div className="advr-recent-row" key={`${h.uid}-${h.rankId}`}>
              <div>
                <span className="ps-strong">{h.name || h.username}</span>
                <span className="ps-muted ps-small"> {h.username}</span>
              </div>
              <span className="advr-rank"><em>{h.rankId}</em>{h.rank}</span>
              <span className="ps-muted ps-small">{moment(h.achieved_at).format("DD MMM YYYY, hh:mm A")}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvisorRanks;
